import { useCallback, useMemo, useState } from "react";
import type { Source } from "../../../types/source.types";
import { trackEvent } from "../../../utils/telemetry";
import type { BillingSummary } from "../../../types/billing.types";
import { getSourceBillingBadge, type SourceBillingBadge } from "./sourceBillingBadge";

export const CATALOGUE_ALL_CATEGORIES = "All";

export interface CatalogueViewViewModel {
  selectedCategory: string;
  categories: string[];
  availableSources: Source[];
  plannedSources: Source[];
  setSelectedCategory: (category: string) => void;
  /** The tier or trial badge for a source under the user's plan, if any. */
  getBillingBadge: (source: Source) => SourceBillingBadge | null;
}

export const useCatalogueViewViewModel = (
  allSources: Source[],
  searchText: string,
  billing?: BillingSummary,
): CatalogueViewViewModel => {
  const [selectedCategory, setCategory] = useState<string>(CATALOGUE_ALL_CATEGORIES);

  const setSelectedCategory = useCallback((category: string) => {
    setCategory(category);
    trackEvent("catalogue.categoryFiltered", { category });
  }, []);

  const categories = useMemo(() => {
    const unique = [
      ...new Set(allSources.map((s) => s.category).filter(Boolean) as string[]),
    ].sort();
    return [CATALOGUE_ALL_CATEGORIES, ...unique];
  }, [allSources]);

  const filteredBySearch = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return allSources;
    return allSources.filter(
      (s) =>
        s.displayName.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q),
    );
  }, [allSources, searchText]);

  const filteredByCategory = useMemo(() => {
    if (selectedCategory === CATALOGUE_ALL_CATEGORIES) return filteredBySearch;
    return filteredBySearch.filter((s) => s.category === selectedCategory);
  }, [filteredBySearch, selectedCategory]);

  const availableSources = useMemo(
    () => filteredByCategory.filter((s) => !s.backlog),
    [filteredByCategory],
  );

  const plannedSources = useMemo(
    () => filteredByCategory.filter((s) => s.backlog),
    [filteredByCategory],
  );

  const getBillingBadge = useCallback(
    (source: Source) => getSourceBillingBadge(source, billing),
    [billing],
  );

  return {
    selectedCategory,
    categories,
    availableSources,
    plannedSources,
    setSelectedCategory,
    getBillingBadge,
  };
};
